<template>
  <div class="flex justify-end">
    <!-- Icon-only trigger (ellipsis) for page headers (MAI-4951). -->
    <!-- Secondary (gray) per MAI-4984 — matches the gray back button. -->
    <Button
      v-if="iconOnly"
      data-testid="actions-menu-button"
      type="button"
      icon="pi pi-ellipsis-h"
      severity="secondary"
      text
      rounded
      aria-label="Actions"
      @click="menu.toggle($event)"
    />
    <Button
      v-else
      data-testid="actions-menu-button"
      type="button"
      label="Actions"
      icon="pi pi-chevron-down"
      icon-pos="right"
      class="!bg-gray-500 !border-gray-600 hover:!bg-gray-600 focus:!border-gray-400 active:!border-gray-400 text-sm"
      @click="menu.toggle($event)"
    />
    <Menu ref="menu" :model="computedMenuItems" :popup="true">
      <template #item="{ item }: { item: MenuItem } & PrimeMenuSlot">
        <hr v-if="item.isSeparator" class="my-2 border-t border-gray-200" />
        <a
          v-else
          v-tooltip.left="
            item.disabled && item.disabledTooltip
              ? {
                  value: item.disabledTooltip,
                  autoHide: false,
                }
              : undefined
          "
          class="flex items-center p-2 cursor-pointer hover:!bg-gray-100"
          :class="[
            {
              'pointer-events-auto': item.disabled && item.disabledTooltip,
              'text-red-500': item.dangerous,
            },
            disabledClass(item),
            item.class,
          ]"
          v-bind="item.dataTestId ? { 'data-testid': item.dataTestId } : {}"
          @click="item.disabled ? (e) => e.preventDefault() : item.command"
        >
          <i :class="[item.icon, 'mr-2']"></i>
          <span class="text-sm">{{ item.label }}</span>
        </a>
      </template>
    </Menu>
  </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Menu, { type MenuSlots } from "primevue/menu";
import Tooltip from "primevue/tooltip";
import type { MenuItem as PrimeMenuItem } from "primevue/menuitem";
import { ref, computed } from "vue";

const vTooltip = Tooltip;

type PrimeMenuSlot = Parameters<MenuSlots["item"]>[0];

interface MenuItem extends PrimeMenuItem {
  label: string;
  icon: string;
  command: () => void;
  disabled?: boolean;
  isSeparator?: boolean;
  dataTestId?: string;
  disabledTooltip?: string | null;
  class?: string;
  disabledClass?: string;
}

interface DangerousAction extends MenuItem {
  dangerous: true;
}

const props = defineProps<{
  menuItems: MenuItem[];
  dangerousAction?: DangerousAction;
  dangerousActions?: DangerousAction[];
  /** Render as a bare ellipsis icon button instead of the labeled button. */
  iconOnly?: boolean;
}>();

const menu = ref();

const separatorItem: MenuItem = {
  label: "Separator",
  icon: "pi pi-fw",
  command: () => {},
  isSeparator: true,
};

const disabledClass = (item: MenuItem) => {
  if (item.disabled) {
    if (item.disabledClass) {
      return item.disabledClass;
    }
    if (item.class) {
      return "opacity-50";
    } else {
      return "opacity-50 cursor-not-allowed";
    }
  }
  return "";
};

const computedMenuItems = computed(() => {
  const allDangerousActions =
    props.dangerousActions ??
    (props.dangerousAction ? [props.dangerousAction] : []);

  if (allDangerousActions.length > 0) {
    const items = [...props.menuItems];
    if (items.length > 0) {
      items.push(separatorItem);
    }
    items.push(...allDangerousActions);
    return items;
  }
  return props.menuItems;
});
</script>
